import React, { PureComponent } from 'react'
import Form, { FormConsumer } from '@active/react-ui/Form'
import FormGroup from '@active/react-ui/FormGroup'
import FormLabel from '@active/react-ui/FormLabel'
import FormField from '@active/react-ui/FormField'
import EmailInput from '@active/react-ui/EmailInput'
import PasswordInput from '@active/react-ui/PasswordInput'
import Button from '@active/react-ui/Button'
import ButtonGroup from '@active/react-ui/ButtonGroup'
import Skeleton from '@active/react-ui/Skeleton'
import { withI18n } from '@active/react-utils/i18n'
import {
  string, bool, func, oneOfType, object, shape,
} from 'prop-types'
import isFunction from 'lodash/isFunction'
import { generateServices } from '../../shared/service'
import { messageUtils } from '../../shared/utils/commonUtils'
import {
  commonServiceConfig,
  resetPasswordServiceConfig,
} from '../../widgets/shared/config'
import { passwordValidator, confirmPasswordValidator } from '../../shared/validator'

import I18nMessage from '../common/I18nMessage'
import './UpdatePassword.scss'

function renderDefaultTitle() {
  return (
    <h1 className="update-password-title">
      <I18nMessage id="passport.updatePassword.title" />
    </h1>
  )
}

const updatePasswordPropTypes = {
  style: shape({}),
  className: oneOfType([object, string]),
  horizontal: bool,
  showFormLabel: bool,
  renderTitle: func,
  renderButtonGroup: func,
  handleFormReset: func,
  handleFormSubmit: func,
  services: shape({
    updatePasswordAPI: func.isRequired,
  }).isRequired,
  actions: shape({
    logoutCallback: func.isRequired,
  }).isRequired,
  login: func.isRequired,
  i18n: func.isRequired,
  token: string,
  showResetBtn: bool,
  showEmailInput: bool,
  realm: string,
}

const updatePasswordProps = {
  style: null,
  className: '',
  renderTitle: null,
  renderButtonGroup: null,
  handleFormReset: null,
  handleFormSubmit: null,
  showFormLabel: true,
  horizontal: true,
  token: '',
  showResetBtn: false,
  showEmailInput: false,
  realm: 'active',
}

export class UpdatePassword extends PureComponent {
  static displayName = 'UpdatePassword'

  static propTypes = updatePasswordPropTypes

  static defaultProps = updatePasswordProps

  constructor(props) {
    super(props)

    this.state = {
      initialValues: null,
    }
  }

  componentDidMount() {
    this.getUserInfoBySSOToken()
  }

  resetErrorMsg = () => {
    this.setState({
    })
  }

  resetInitialValues = () => {
    const { initialValues } = this.state
    this.setState({
      initialValues: {
        ...initialValues,
        old_pwd: '',
        new_pwd: '',
        confirmNewPassword: '',
      },
    })
  }

  updateInitialValues = values => {
    this.setState({
      initialValues: {
        ...values,
      },
    })
  }

  getUserInfoBySSOToken = () => {
    const { login, realm } = this.props
    const commonServices = generateServices(commonServiceConfig, {
      ...this.props,
      realm,
    })

    commonServices.getLoggedinUserInfo({
      headers: {
        Accept: 'application/json',
        'Content-type': 'application/json',
      },
      resolve: ({ active_username: activeUsername }) => {
          this.setState({
            initialValues: {
              email: activeUsername,
              old_pwd: '',
              new_pwd: '',
              confirmNewPassword: '',
            },
          })
      },
      reject: () => {
        login()
      },
    })
  }

  handleFormSubmit = values => {
    const {
      services, token, i18n, handleFormSubmit, realm,
    } = this.props

    this.updateInitialValues(values)

    if (isFunction(handleFormSubmit)) {
      return handleFormSubmit(values)
    }

    const resetPasswordServices = generateServices(resetPasswordServiceConfig, {
      ...this.props,
      realm,
    })

    return services.updatePasswordAPI({
      params: { ...values, token },
      // eslint-disable-next-line no-shadow
      resolve: ({ success, error, error_description: errorDescription }) => {
        if (success === true) {
          resetPasswordServices.refreshPassword({
            params: { username: values.email },
            resolve: this.handleSuccess,
            reject: this.handleSuccess,
          })
          this.resetErrorMsg()
          this.resetInitialValues()
        } else if (error === 'PSW_NOT_QUALFIED') {
          const message = (<I18nMessage id={i18n('passport.common.notice.pwdDescription')} />) || i18n('passport.error.server.default')
          messageUtils.alert(message, { pureString: true, type: 'error', dismissible: false })
        } else {
          messageUtils.alert(errorDescription || i18n('passport.error.server.default'), { pureString: true, type: 'error', dismissible: false })
        }
      },
      reject: error => {
        messageUtils.alert((error && error.error_description) || i18n('passport.error.server.default'), { pureString: true, type: 'error', dismissible: false })
      },
    })
  }

  handleSuccess = () => {
    const {
      i18n, actions: { logoutCallback },
    } = this.props
    messageUtils.alert(i18n('passport.resetPassword.notice.success'), {
      pureString: true,
      dismissible: false,
      timeout: 7000,
      type: 'success',
      onClose: logoutCallback,
    })
  }

  handleFormReset = ({ handleReset }) => {
    const { handleFormReset } = this.props

    handleReset()
    this.resetErrorMsg()
    this.resetInitialValues()
    isFunction(handleFormReset) && handleFormReset()
  }

  renderButtonGroup = () => {
    const { horizontal, showResetBtn } = this.props

    return (
      <FormConsumer>
        {({ form: { handleReset, dirty, isSubmitting } }) => (
          <ButtonGroup
            horizontal={horizontal}
            className="btn-group"
          >
            {
              showResetBtn && (
                <Button
                  type="secondary"
                  onClick={() => this.handleFormReset({ handleReset })}
                  disabled={!dirty || isSubmitting}
                >
                  <I18nMessage id="passport.components.updatePassword.reset" />
                </Button>
              )
            }
            <Button disabled={isSubmitting} submit type="primary">
              <I18nMessage id="passport.components.updatePassword.submit" />
            </Button>
          </ButtonGroup>
        )}
      </FormConsumer>
    )
  }

  validatePasswordField = password => {
    const { i18n } = this.props
    const { initialValues: { email } } = this.state
    return i18n(passwordValidator(password, email, i18n))
  }

  validateConfirmPasswordField = (
    confirmPassword,
    { new_pwd: { value } },
  ) => {
    const { i18n } = this.props

    return i18n(confirmPasswordValidator(confirmPassword, value))
  }

  render() {
    const {
      style, horizontal, className, showFormLabel, i18n, renderButtonGroup, renderTitle, showEmailInput,
    } = this.props
    const { initialValues } = this.state

    if (!initialValues) {
      return <Skeleton className="passport-skeleton" />
    }

    return (
      <div className={`update-password ${className}`} style={style}>
        {isFunction(renderTitle) ? renderTitle() : renderDefaultTitle()}
        <div className="update-password-tip">
          <I18nMessage id="passport.components.updatePassword.tip" />
        </div>
        <div className="update-password-required-label">
          <I18nMessage id="passport.common.asterisk.significance" />
          <span className="required-strong">*</span>
        </div>
        <Form
          initialValues={initialValues}
          enableReinitialize
          horizontal={horizontal}
          onSubmit={this.handleFormSubmit}
        >
          {showEmailInput && (
            <FormGroup name="email" required>
              {showFormLabel && (
                <FormLabel>
                  <I18nMessage id="passport.components.updatePassword.emailAddress" />
                </FormLabel>
              )}
              <FormField
                readOnly
                as={EmailInput}
                requiredError={i18n(
                  'passport.components.updatePassword.emailInputRequiredErrorr',
                )}
                invalidError={i18n(
                  'passport.components.updatePassword.emailInputInvalidError',
                )}
              />
            </FormGroup>
          )}
          <FormGroup name="old_pwd" required>
            {showFormLabel && (
              <FormLabel>
                <I18nMessage id="passport.components.updatePassword.oldPassword" />
              </FormLabel>
            )}
            <FormField
              as={PasswordInput}
              requiredError={i18n(
                'passport.components.updatePassword.oldPasswordRequiredError',
              )}
            />
          </FormGroup>
          <FormGroup name="new_pwd" required>
            {showFormLabel && (
              <FormLabel>
                <I18nMessage id="passport.components.updatePassword.newPassword" />
              </FormLabel>
            )}
            <FormField
              as={PasswordInput}
              aria-describedby="new_pwd"
              requiredError={i18n(
                'passport.components.updatePassword.newPasswordRequiredError',
              )}
              renderError={({ error }) => <I18nMessage id={error} />}
              hint={<span id="new_pwd"><I18nMessage id="passport.common.notice.passwordRules" /></span>}
              validate={this.validatePasswordField}
            />
          </FormGroup>
          <FormGroup name="confirmNewPassword" required>
            {showFormLabel && (
              <FormLabel>
                <I18nMessage id="passport.components.updatePassword.confirmNewPassword" />
              </FormLabel>
            )}
            <FormField
              as={PasswordInput}
              requiredError={i18n(
                'passport.components.updatePassword.confirmNewPasswordRequiredError',
              )}
              subscribe={['new_pwd']}
              validate={this.validateConfirmPasswordField}
            />
          </FormGroup>
          {isFunction(renderButtonGroup) ? renderButtonGroup() : this.renderButtonGroup()}
        </Form>
      </div>
    )
  }
}

export default withI18n(UpdatePassword)
